import { useState, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { MultiSelect } from '../../../components/common/MultiSelect/MultiSelect';
import { ORGANIZATIONS_LIST_PAGE_SIZE } from '../../../constants/organization';
import { useOrganizationsQuery } from '../../../hooks/useDashboardQueries';
import { OrganizationOption } from '../../../types/organization';

interface OrganizationMultiSelectProps {
  selectedOrganizations: OrganizationOption[];
  onChange: (organizations: OrganizationOption[]) => void;
}

export const OrganizationMultiSelect = memo(function OrganizationMultiSelect({
  selectedOrganizations,
  onChange,
}: OrganizationMultiSelectProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data, isLoading: loading } = useOrganizationsQuery(
    1,
    ORGANIZATIONS_LIST_PAGE_SIZE,
    '',
    isOpen,
  );

  const options: OrganizationOption[] = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.data ?? [])
      // Legacy documents can lack a name despite the schema.
      .map((o) => ({ id: o.id, name: o.name || o.id }))
      .filter((o) => !q || o.name.toLowerCase().includes(q));
  }, [data, search]);

  return (
    <MultiSelect
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      search={search}
      onSearchChange={setSearch}
      options={options}
      selected={selectedOrganizations}
      onChange={onChange}
      loading={loading}
      getId={(org) => org.id}
      getLabel={(org) => org.name}
      icon="corporate_fare"
      labels={{
        toggle:
          selectedOrganizations.length === 0
            ? t('dashboard.metrics.allOrganizations')
            : t('dashboard.metrics.organizationsCount', {
                count: selectedOrganizations.length,
              }),
        toggleAria: t('dashboard.metrics.selectOrganizationsAria'),
        searchPlaceholder: t('dashboard.metrics.searchOrganizationsPlaceholder'),
        loading: t('dashboard.metrics.loading'),
        empty: t('dashboard.metrics.noOrganizationsFound'),
      }}
    />
  );
});

export default OrganizationMultiSelect;
